/**
 * Voice recording mic button with long-press detection and gesture support.
 *
 * How it works (touch — IS_TOUCH_ENV):
 * 1. On pointerdown: requests mic permission, starts a 500ms long-press timer.
 * 2. After 500ms: starts recording and calls onGestureStart so the parent
 *    can begin tracking slide/lock gestures via useRecordingGesture.
 * 3. The parent handles pointerup/move via the gesture hook — this component
 *    only handles the initial press and long-press threshold.
 *
 * How it works (non-touch / mouse — !IS_TOUCH_ENV):
 * 1. On pointerdown: requests mic permission (instant if already granted).
 * 2. Immediately starts recording and calls onImmediateLock so the parent
 *    enters locked (hands-free) mode right away — no hold required.
 *
 * Key features:
 * - 44×44px minimum tap target (visual icon stays small)
 * - Telegram-style scale-up animation when recording starts
 * - Permission flow coordinated with requestMic()
 * - pointerDownRef tracks whether the user is still pressing — guards against
 *   recording starting after a permission dialog (user no longer holding)
 * - If the user releases during the startRecording() async gap, onImmediateSend
 *   fires so the parent can stop+send immediately (race condition guard, touch only)
 */

import { useRef, useCallback, useEffect } from "react";
import { Mic } from "lucide-react";
import { cn } from "@/lib/utils";

import type { RecordingStatus } from "@/lib/voice";
import { LONG_PRESS_THRESHOLD } from "@/lib/voice";
import { IS_TOUCH_ENV } from "@/lib/environmentDetector";
import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";

interface RecordVoiceMessageButtonProps {
  startRecording: () => Promise<void>;
  requestMic: () => Promise<void>;
  voiceRecordingState: RecordingStatus;
  /** Called after long-press fires and recording starts — parent should begin gesture tracking (touch only) */
  onGestureStart: (origin: { x: number; y: number }) => void;
  /** Called when recording started but the user released before gesture tracking began (touch only) */
  onImmediateSend: () => void;
  /** Called on non-touch devices: recording started, parent should enter lock mode immediately */
  onImmediateLock: () => void;
  /** Additional classes for the outer wrapper */
  className?: string;
}

export default function RecordVoiceMessageButton({
  startRecording,
  voiceRecordingState,
  requestMic,
  onGestureStart,
  onImmediateSend,
  onImmediateLock,
  className: wrapperClassName,
}: Readonly<RecordVoiceMessageButtonProps>) {
  const { toast } = useToast();
  const { t } = useTranslation();

  // Tracks whether the user's pointer is currently down on the button.
  // Guards against starting recording after a permission dialog dismissal
  // (the user is no longer pressing) and against the startRecording() race.
  const pointerDownRef = useRef(false);

  // Long-press timer ID
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store the pointer origin coordinates for forwarding to gesture hook after threshold
  const pendingOriginRef = useRef<{ x: number; y: number } | null>(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Clean up timer on unmount to prevent stale callbacks
  useEffect(() => {
    return () => clearLongPressTimer();
  }, [clearLongPressTimer]);

  const handlePointerDown = useCallback(
    async (e: React.PointerEvent) => {
      e.preventDefault(); // prevent iOS context menu

      pointerDownRef.current = true;
      // Store coordinates (not the synthetic event — it may be recycled by React)
      pendingOriginRef.current = { x: e.clientX, y: e.clientY };

      // Step 1: Request mic permission (returns instantly if already acquired)
      try {
        await requestMic();
      } catch (error) {
        toast({
          duration: 1500,
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
        pendingOriginRef.current = null;
        return;
      }

      // If the user released while the permission dialog was showing, abort.
      // They must press again after granting permission.
      if (!pointerDownRef.current) {
        pendingOriginRef.current = null;
        return;
      }

      if (!IS_TOUCH_ENV) {
        // ── Non-touch (mouse): single click → lock mode immediately ──
        // No long-press timer needed — just start recording and lock.
        try {
          await startRecording();
        } catch {
          pendingOriginRef.current = null;
          return;
        }
        pendingOriginRef.current = null;
        pointerDownRef.current = false;
        onImmediateLock();
        return;
      }

      // ── Touch: 500ms long-press → hold mode ──

      // Step 2: Start long-press timer
      longPressTimerRef.current = setTimeout(async () => {
        longPressTimerRef.current = null;

        // Double-check pointer is still down (e.g. pointercancel during wait)
        if (!pointerDownRef.current) {
          pendingOriginRef.current = null;
          return;
        }

        try {
          await startRecording();
        } catch {
          return;
        }

        // Forward the stored coordinates to the gesture system
        // so it can set the drag origin
        if (pendingOriginRef.current && pointerDownRef.current) {
          // Clear pointerDownRef — the gesture hook's window-level listeners
          // take over pointer tracking from here. If we don't clear this,
          // pointer capture can prevent the button's onPointerUp from firing,
          // leaving pointerDownRef stuck at true.
          pointerDownRef.current = false;
          onGestureStart(pendingOriginRef.current);
          pendingOriginRef.current = null;
        } else {
          // User released during the startRecording() async gap.
          // Recording is active but gesture tracking never started — send immediately.
          pendingOriginRef.current = null;
          onImmediateSend();
        }
      }, LONG_PRESS_THRESHOLD);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      requestMic,
      startRecording,
      onGestureStart,
      onImmediateSend,
      onImmediateLock,
    ],
  );

  // Handle pointer release or cancellation — cancel timer if still pending
  const handlePointerRelease = useCallback(() => {
    pointerDownRef.current = false;
    if (longPressTimerRef.current) {
      clearLongPressTimer();
      pendingOriginRef.current = null;
    }
  }, [clearLongPressTimer]);

  const isRecording = voiceRecordingState === "recording";

  return (
    <div
      role="button"
      tabIndex={0}
      // 44×44px tap target wrapper — visual icon is smaller inside
      className={cn(
        "relative flex size-10 items-center justify-center rounded-full bg-muted",
        wrapperClassName,
      )}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerRelease}
      onPointerCancel={handlePointerRelease}
      style={{
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
        touchAction: "none", // prevent browser gestures during recording
      }}
    >
      {/* Mic icon with Telegram-style scale-up on recording — absolute to avoid layout shift */}
      <div
        className={cn(
          "absolute flex items-center justify-center rounded-full transition-all duration-300 ease-out",
          isRecording
            ? "size-14 bg-primary text-primary-foreground shadow-lg"
            : "size-6 bg-transparent",
        )}
      >
        <Mic
          className={cn(
            "transition-all duration-200",
            isRecording ? "size-6" : "size-5",
          )}
        />
      </div>
    </div>
  );
}
